import {
    HISTORY_CUTOFF_YEAR_MONTH,
    PAYMENT_GATEWAY_LABELS,
    PAYMENT_STATUS_LABELS,
    PAYMENT_TYPE_LABELS,
    RAW_STATUS_LABELS,
} from 'src/redux/panel_transactions/panel_transactions.constants';
import {
    PanelPaymentStatus,
    PanelTransactionAgent,
    PanelTransactionsSummary,
} from 'src/redux/panel_transactions/panel_transactions.types';
import { methodsName, payment_method } from 'src/constants/ShoppingCart';

/** Marcador de las columnas que todavía no tienen fuente de datos. */
export const PENDING_VALUE = '—';

/**
 * Sin decimales forzados: los montos del panel son cifras redondas y los ceros
 * solo ensucian la celda. Si una operación trae centavos, se muestran.
 */
const amountFormatter = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

export const formatAmount = (amount: number | null): string =>
    amount == null ? PENDING_VALUE : `$${amountFormatter.format(amount)} MXN`;

/** Suma de los tres estados. Es el monto del encabezado: sigue todos los filtros. */
export const totalSummaryAmount = (summary: PanelTransactionsSummary): number =>
    summary.in_process.amount + summary.successful.amount + summary.failed.amount;

/**
 * Anchos compartidos entre el encabezado y la tabla. El encabezado usa el mismo
 * grid que las columnas para que la suma caiga sobre *Monto y método* y el
 * conteo quede pegado a su izquierda.
 */
export type TransactionsColumnLayout = {
    template: string;
    columns: { key: string; width: string }[];
};

export const buildTransactionsColumnLayout = ({
    showCustomer,
    showOrigin,
}: {
    showCustomer: boolean;
    showOrigin: boolean;
}): TransactionsColumnLayout => {
    const columns: { key: string; fr: number; named?: boolean }[] = [
        { key: 'tx', fr: 12, named: true },
    ];
    if (showCustomer) columns.push({ key: 'customer', fr: 12, named: true });
    if (showOrigin) columns.push({ key: 'origin', fr: 10, named: true });
    columns.push({ key: 'product', fr: 16, named: true });
    columns.push({ key: 'amount', fr: 13, named: true });
    columns.push({ key: 'pago', fr: 10, named: true });
    columns.push(
        { key: 'activation', fr: 6 },
        { key: 'subscription', fr: 6 },
        { key: 'invoice', fr: 6 },
    );

    const total = columns.reduce((sum, column) => sum + column.fr, 0);
    const sized = columns.map((column) => ({
        key: column.key,
        named: Boolean(column.named),
        width: `${((column.fr / total) * 100).toFixed(4)}%`,
    }));

    return {
        template: sized
            .map((column) =>
                column.named ? `[${column.key}] ${column.width}` : column.width,
            )
            .join(' '),
        columns: sized.map(({ key, width }) => ({ key, width })),
    };
};

/**
 * Fecha y hora en México. El API manda `transactions.created` en ISO naive
 * (UTC de Lambda, sin `Z`); si se parsea como local, CDMX ve +6 h.
 */
const MEXICO_TIME_ZONE = 'America/Mexico_City';

const parseTransactionCreated = (value: string): Date => {
    const trimmed = value.trim();
    const last = trimmed.slice(-1);
    if (last === 'Z' || last === 'z' || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
        return new Date(trimmed);
    }
    const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    return new Date(`${iso}Z`);
};

export const formatDateTime = (value: string | null): string => {
    if (!value) return PENDING_VALUE;
    const parsed = parseTransactionCreated(value);
    if (Number.isNaN(parsed.getTime())) return value;
    const mexico = { timeZone: MEXICO_TIME_ZONE };
    const date = parsed.toLocaleDateString('es-MX', {
        ...mexico,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    const time = parsed.toLocaleTimeString('es-MX', {
        ...mexico,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
    return `${date} ${time}`;
};

/**
 * Meses escritos acá a propósito, en lugar de pedírselos al locale: el nombre
 * largo de `toLocaleDateString` sale de los datos de ICU del entorno, y con un
 * Node sin ICU completo este aviso diría "January de 2026". Son doce palabras
 * que no cambian nunca; no vale la pena que dependan del build.
 */
const MONTH_NAMES = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
];

/**
 * Meses del filtro de periodo: desde el corte del histórico (enero 2026) hasta
 * el mes en curso. "Todos" es el histórico completo; cada mes acota a ese
 * calendario. El año no va en la etiqueta mientras el listado solo cubra 2026.
 */
export const buildPeriodOptions = (
    now: Date = new Date(),
    fromYearMonth = HISTORY_CUTOFF_YEAR_MONTH,
): { value: string; label: string }[] => {
    const [fromYear, fromMonth] = fromYearMonth.split('-').map(Number);
    const options: { value: string; label: string }[] = [{ value: '', label: 'Todos' }];
    const start = fromYear * 12 + fromMonth;
    const end = now.getFullYear() * 12 + (now.getMonth() + 1);

    for (let cursor = start; cursor <= end; cursor += 1) {
        const year = Math.floor((cursor - 1) / 12);
        const month = ((cursor - 1) % 12) + 1;
        options.push({
            value: `${year}-${String(month).padStart(2, '0')}`,
            label: MONTH_NAMES[month - 1],
        });
    }

    return options;
};

/**
 * Mes y año del corte del histórico, para el aviso al pie de la tabla
 * ("Enero de 2026"). El mes va completo: el aviso se lee una sola vez y
 * abreviarlo no ahorra nada en una línea que ya tiene espacio de sobra.
 */
export const formatHistoryFrom = (value: string | null): string | null => {
    if (!value) return null;
    // Sin `Z` ni offset: se interpreta como fecha local, así que el mes no se
    // corre un día por zona horaria.
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return `${MONTH_NAMES[parsed.getMonth()]} de ${parsed.getFullYear()}`;
};

/** Nombre de la pasarela; una pasarela nueva se muestra por su id, no como "—". */
const formatPaymentGateway = (gateway: number): string =>
    PAYMENT_GATEWAY_LABELS[gateway] ?? `Pasarela ${gateway}`;

/**
 * Forma de pago dentro de la pasarela. Un `method` sin etiqueta cae a su valor
 * crudo para que se vea en el panel antes de tener traducción.
 */
const formatPaymentType = (method: string | null): string | null =>
    method ? PAYMENT_TYPE_LABELS[method] ?? method : null;

/** OpenPay en el carrito: tarjeta (3), transferencia (4) y pago en tienda (6). */
const OPENPAY_GATEWAY_IDS = new Set<number>([
    payment_method[methodsName.CARD],
    payment_method[methodsName.TRANSFER],
    payment_method[methodsName.CASH],
    7,
]);

/**
 * Cuando la transferencia o el depósito se guardaron como pasarela propia y
 * `method` viene vacío, la forma se infiere del id: si no, la celda diría
 * "OpenPay" a secas y no se distinguiría de una tarjeta.
 */
const METHOD_INFERRED_BY_GATEWAY: Record<number, string> = {
    [payment_method[methodsName.TRANSFER]]: 'Transferencia',
    [payment_method[methodsName.CASH]]: 'Depósito en efectivo',
};

/** Paypal y Pago Directo no tienen un segundo nivel que aporte al comercial. */
const GATEWAYS_WITHOUT_METHOD_LINE = new Set<number>([
    payment_method[methodsName.PAYPAL],
    payment_method[methodsName.DIRECT],
]);

const resolveSpecificMethod = (
    gateway: number | null,
    method: string | null,
): string | null => {
    if (gateway != null && GATEWAYS_WITHOUT_METHOD_LINE.has(gateway)) return null;
    const fromMethod = formatPaymentType(method);
    if (fromMethod) return fromMethod;
    if (gateway != null && OPENPAY_GATEWAY_IDS.has(gateway)) {
        return METHOD_INFERRED_BY_GATEWAY[gateway] ?? null;
    }
    return null;
};

/**
 * Las dos mitades del cobro en una línea: el canal y, en OpenPay y MercadoPago,
 * cómo se pagó ("OpenPay · Tarjeta", "MercadoPago · Depósito en efectivo").
 *
 * Se omite la segunda mitad cuando la primera ya la contiene (un link de
 * MercadoPago todavía sin pagar es "MercadoPago" a secas) y en Paypal / Pago
 * Directo, que no tienen un segundo nivel útil.
 */
export const formatPaymentMethod = (
    gateway: number | null,
    method: string | null,
): string => {
    const typeLabel = resolveSpecificMethod(gateway, method);
    if (gateway == null) return typeLabel ?? PENDING_VALUE;

    const gatewayLabel = formatPaymentGateway(gateway);
    if (!typeLabel || gatewayLabel === typeLabel || gatewayLabel.startsWith(typeLabel)) {
        return gatewayLabel;
    }
    return `${gatewayLabel} · ${typeLabel}`;
};

export const formatPaymentStatus = (status: PanelPaymentStatus | null): string =>
    status ? PAYMENT_STATUS_LABELS[status] : PENDING_VALUE;

/**
 * Detalle del estado crudo. Distingue casos que el estado agrupado junta
 * ("Cancelada" y "Reembolsada" son ambos Fallido) y sirve de tooltip.
 */
export const formatRawStatus = (rawStatus: number | null): string =>
    rawStatus == null ? PENDING_VALUE : RAW_STATUS_LABELS[rawStatus] ?? String(rawStatus);

export const formatCustomer = (name: string | null, email: string | null): string =>
    name || email || PENDING_VALUE;

/** Código de la transacción tal como lo muestra la tabla. */
export const formatTransactionCode = (transactionId: number): string =>
    `TX-${transactionId}`;

/**
 * Número de transacción tipeado por una persona.
 *
 * Se aceptan todas las formas en las que ese número llega, porque llega copiado
 * de un reclamo: `TX-48135`, `48135`, `#48135`, con espacios. Exigir un formato
 * agregaría un error que la pantalla puede resolver sola.
 *
 * `null` es "acá no hay un número que buscar" — lo usa el campo vacío para
 * volver al listado y el texto sin dígitos para marcarse como inválido.
 */
export const parseTransactionId = (value: string): number | null => {
    const digits = value.replace(/\D/g, '');
    if (digits === '') return null;

    const parsed = Number(digits);
    // Un número que no cabe en un entero seguro no es un id: llegó de un pegado
    // que trajo de más, y mandarlo devolvería un vacío inexplicable.
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Correo tipeado en el filtro del listado. Vacío es "sin filtro" (Estado B), no
 * un error: es como se vuelve a la cartera. `null` es un texto que no es correo,
 * para marcar el campo en lugar de consultar.
 *
 * El mismo patrón que el buscador central de `/retention`, sin comprobar que el
 * cliente exista: acá un correo sin filas es un vacío honesto del listado.
 */
const CUSTOMER_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const parseCustomerEmail = (value: string): string | null => {
    const trimmed = value.trim();
    if (trimmed === '') return '';
    return CUSTOMER_EMAIL_REGEX.test(trimmed) ? trimmed : null;
};

/**
 * Con quién hablar de esa venta: el nombre del comercial y, si no lo tiene
 * registrado, su correo. Devuelve `null` cuando no hay a quién nombrar, para que
 * la celda muestre solo la etiqueta del origen en lugar de un "—" que se leería
 * como un dato faltante de la operación.
 */
export const formatOriginAgent = (agent: PanelTransactionAgent | null): string | null =>
    agent ? agent.name || agent.email || null : null;

/**
 * Tooltip de esa línea: nombre y correo juntos. Sirve para dos cosas a la vez —
 * dar el correo con el que contactar al comercial, y dejar legible el nombre
 * completo cuando la columna lo recorta.
 */
export const formatOriginAgentHint = (
    agent: PanelTransactionAgent | null,
): string | undefined => {
    if (!agent) return undefined;
    const parts = [agent.name, agent.email].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
};
